import { useState } from "react";
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Label,
  Message,
  Modal,
} from "semantic-ui-react";
import { Card, Field, Page, PageHeader, ui } from "../components/ui";
import config from "../config";
import { colors } from "../style";
import { useAuth } from "../contexts/AuthContext";
import { useDisconnectApp } from "../hooks/useDisconnectApp";
import { useSaveStreetConfig } from "../hooks/useSaveStreetConfig";
import arborLogo from "../images/arbor-logo.png";
import streetLogo from "../images/street-logo.png";
import trackerRmsLogo from "../images/tracker-rms-logo.png";
import zohoDeskLogo from "../images/zoho-desk-logo.png";
import zohoLogo from "../images/zoho-logo.png";

// only the apps that receive Sidekick call transcriptions
const apps = [
  {
    integrationName: "arbor",
    featureName: "arbor",
    name: "Arbor",
    description:
      "Add call transcripts to the matching Arbor contact as a call record.",
    docsUrl: "",
    alwaysShow: true,
    // linked once for the whole organisation, so only admins manage it
    adminFeature: true,
    connectButton: (
      <Label
        image
        size="big"
        as="a"
        className="sk-connect"
        href={`${config.api_url}/login/arbor`}
        style={{ paddingTop: "0.49em" }}
      >
        <img src={arborLogo} alt="Arbor logo" />
        Connect Arbor
      </Label>
    ),
  },
  {
    integrationName: "street",
    name: "Street",
    description:
      "Add call summaries and transcripts to the matching Street contact as notes.",
    docsUrl: "https://docs.connect.cosoft.co.uk/apps/street",
    connectButton: <StreetIntegration />,
    adminFeature: true,
    // Street reveals its token once, so an accidental disconnect means going back to
    // Street for a new one while the integration is down for the whole organisation
    confirmDisconnect: true,
  },
  {
    integrationName: "zoho",
    featureName: "zoho",
    name: "Zoho CRM",
    description:
      "Add call transcripts and summaries to the caller's Zoho CRM account as notes. Ask your reseller or support to switch this on for your organisation.",
    connectButton: (
      <Label
        image
        size="big"
        as="a"
        className="sk-connect"
        href={`${config.api_url}/login/zoho`}
        style={{ paddingTop: "0.49em" }}
      >
        <img src={zohoLogo} alt="Zoho logo" />
        Connect Zoho CRM
      </Label>
    ),
  },
  {
    integrationName: "zoho-desk",
    featureName: "zoho-desk",
    name: "Zoho Desk",
    description:
      "Add call transcripts and summaries to the call's Zoho Desk ticket as private comments.",
    connectButton: (
      <Label
        image
        size="big"
        as="a"
        className="sk-connect"
        href={`${config.api_url}/login/zoho-desk`}
        style={{ paddingTop: "0.49em" }}
      >
        <img src={zohoDeskLogo} alt="Zoho desk logo" />
        Connect Zoho Desk
      </Label>
    ),
  },
  {
    integrationName: "tracker-rms",
    featureName: "tracker-rms",
    name: "Tracker RMS",
    description:
      "Add call transcripts and meeting notes to the matching Tracker RMS contact as an activity.",
    connectButton: (
      <Label
        image
        size="big"
        as="a"
        className="sk-connect"
        href={`${config.api_url}/login/tracker-rms`}
        style={{ paddingTop: "0.49em" }}
      >
        <img src={trackerRmsLogo} alt="Tracker RMS logo" />
        Connect Tracker RMS
      </Label>
    ),
  },
];

// the cards this user can see; the nav hides Apps when there are none
export function useVisibleApps() {
  const { apiUser, hasFeature } = useAuth();

  return apps.filter(
    (x) =>
      (x.featureName === undefined ||
        hasFeature([x.featureName]) ||
        x.alwaysShow) &&
      (!x.adminFeature || apiUser?.is_admin),
  );
}

function Apps() {
  const { hasIntegration } = useAuth();
  const visibleApps = useVisibleApps();

  return (
    <Page>
      <PageHeader
        title="Apps"
        subtitle="Link the apps that should receive your call transcripts and summaries."
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: "16px",
        }}
      >
        {visibleApps
          .sort(
            (a, b) =>
              Number(!!hasIntegration([b.integrationName])) -
              Number(!!hasIntegration([a.integrationName])),
          )
          .map((app) => (
            <AppCard
              key={app.integrationName}
              connected={hasIntegration([app.integrationName])}
              {...app}
            />
          ))}
      </div>
    </Page>
  );
}

function AppCard({
  integrationName,
  name,
  description,
  docsUrl,
  connected,
  connectButton,
  confirmDisconnect,
}) {
  const [isHovering, setIsHovering] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { disconnect, isDisconnecting } = useDisconnectApp(integrationName);

  return (
    <Card
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        // moving off the card disarms a pending confirm
        setConfirming(false);
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        borderColor: isHovering ? colors.borderStrong : colors.border,
        transition: "border-color 100ms ease-in-out",
      }}
    >
      <div style={{ flex: 1 }}>
        <h2 style={ui.sectionTitle}>
          {connected && <Icon name="check circle" color="green" />} {name}
        </h2>
        <p style={{ ...ui.muted, margin: "8px 0", fontSize: "14px" }}>
          {description}
        </p>

        {docsUrl && (
          <a href={docsUrl} target="_blank" rel="noreferrer">
            Learn more
          </a>
        )}
      </div>
      <div
        style={{ borderTop: `1px solid ${colors.border}`, paddingTop: "12px" }}
      >
        <div>
          {connected ? (
            <Button
              color="red"
              // cards that can't be reconnected without fresh credentials ask twice
              onClick={
                confirmDisconnect && !confirming
                  ? () => setConfirming(true)
                  : () => disconnect()
              }
              disabled={isDisconnecting}
              loading={isDisconnecting}
            >
              {confirming ? "Confirm disconnect" : "Disconnect"}
            </Button>
          ) : (
            <>{connectButton}</>
          )}
        </div>
      </div>
    </Card>
  );
}

function StreetIntegration() {
  const [open, setOpen] = useState(false);
  const [apiToken, setApiToken] = useState("");
  const [environment, setEnvironment] = useState("production");
  const { saveStreetConfig, isSaving, error, clearError } = useSaveStreetConfig(
    {
      onSaved: () => {
        setApiToken("");
        setEnvironment("production");
        setOpen(false);
      },
    },
  );

  const handleSubmit = () => {
    saveStreetConfig({
      api_token: apiToken,
      environment: environment,
    });
  };

  const handleClose = () => {
    setOpen(false);
    setApiToken("");
    clearError();
  };

  return (
    <Modal
      onClose={handleClose}
      onOpen={() => setOpen(true)}
      open={open}
      trigger={
        <Label
          image
          size="big"
          as="a"
          className="sk-connect"
          style={{ paddingTop: "0.49em" }}
        >
          <img src={streetLogo} alt="Street logo" />
          Connect Street
        </Label>
      }
    >
      <Modal.Header>Street Integration</Modal.Header>
      <Modal.Content>
        <Modal.Description>
          <p>
            If you do not have the below information you can{" "}
            <a
              href="https://docs.connect.cosoft.co.uk/apps/street"
              target="_blank"
              rel="noreferrer"
            >
              follow the guide here to retrieve it
            </a>
            .
          </p>

          <Form onSubmit={(e) => e.preventDefault()}>
            {/* masked so it isn't left on screen during a screen share */}
            <Field variant="form" label="API token">
              <input
                placeholder="Enter Street API token"
                value={apiToken}
                type="password"
                onChange={(e) => {
                  clearError();
                  setApiToken(e.target.value.trim());
                }}
              />
            </Field>

            <Field variant="form" label="Environment">
              <Dropdown
                value={environment}
                onChange={(e, { value }) => {
                  clearError();
                  setEnvironment(value);
                }}
                fluid
                selection
                options={[
                  {
                    key: "production",
                    text: "Production (street.co.uk)",
                    value: "production",
                  },
                  {
                    key: "demo",
                    text: "Sandbox (demo.street.co.uk)",
                    value: "demo",
                  },
                ]}
              />
            </Field>

            {error.length > 0 && (
              <Message negative>
                <p>{error}</p>
              </Message>
            )}
          </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          content="Save"
          labelPosition="right"
          icon="checkmark"
          loading={isSaving}
          disabled={isSaving}
          onClick={handleSubmit}
          primary
        />
      </Modal.Actions>
    </Modal>
  );
}

export default Apps;
